import React, { useEffect, useState, useRef } from 'react';
import {
  CarouselPresentationContainer,
  CarouselPresentationContainerProps,
} from '../CarouselPresentationContainer/CarouselPresentationContainer';
import { palette } from '@jbhi-fi/lanyard-ui/themes';
import { styles } from './ProductCarousel.css';
import { Carousel, CarouselProps } from '@jbhi-fi/lanyard-ui/components/Carousel';
import { useProductContext, withProductProvider, WithProductProviderProps } from '../context/ProductProvider';
import { AlgoliaProductHit } from '@jbhi-fi/apis/src/algolia/types/AlgoliaProductHit';
import { useBreakpoint } from '@jbhi-fi/lanyard-ui/utils/useBreakpoint';
import { HeadingDivider } from '../HeadingDivider/HeadingDivider';
import { ProductFallback, ProductFallbackProps } from '../ProductFallback/ProductFallback';
import { NoResultsPlaceholder } from '../NoResultsPlaceholder/NoResultsPlaceholder';
import { classNames } from '@jbhi-fi/lanyard-ui/global/utils';
import { useIsOnScreen } from '../hooks/useIsOnScreen/useIsOnScreen';
import { trackViewItemList } from '@jbhi-fi/utils/src/ga4Tracking/functions/trackViewItemList';
import { transformAlgoliaHitForTracking } from '../Algolia/transformers/transformAlgoliaHitForTracking';
import { GoToLink, GoToLinkProps } from '../GoToLink/GoToLink';
import { useEventsContext } from '../context/Events';
import { getStoreCurrency } from '@jbhi-fi/utils/src/shopify/getStoreCurrency';

type SwiperClass = Parameters<Exclude<CarouselProps['onSwiper'], undefined>>[0];

const LOADING_PRODUCTS: Array<null> = Array.from(Array(8)).fill(null);
const DEFAULT_MAX_NUM_PRODUCTS = 20;

type Props = Omit<CarouselProps, 'children'> & {
  heading?: string;
  ctaButton?: React.ReactNode;
  linkProps?: Omit<GoToLinkProps, 'className'>;
  renderProduct: (data: { hit: AlgoliaProductHit; position: number }) => React.ReactNode;
  noResultsPlaceholder?: React.ReactNode;
  productCardSkeletonProps?: ProductFallbackProps['productCardSkeletonProps'];
  carouselClassName?: string;
  shouldTrackImpressions?: boolean;
  trackingListName?: string;
  maxNumProducts?: number;
  extraSlideStart?: React.ReactNode;
};

export type ProductCarouselProps = WithProductProviderProps<Props>;

export const ProductCarousel = withProductProvider(
  ({
    heading,
    ctaButton,
    linkProps,
    renderProduct,
    noResultsPlaceholder,
    productCardSkeletonProps = {},
    onSwiper,
    className,
    carouselClassName,
    shouldTrackImpressions = false,
    trackingListName = heading,
    maxNumProducts = DEFAULT_MAX_NUM_PRODUCTS,
    extraSlideStart,
    ...rest
  }: Props) => {
    const { products: allProducts = [], isLoading } = useProductContext();
    const { postEvent } = useEventsContext();
    const [swiper, setSwiper] = useState<SwiperClass | undefined>();
    const hasNoProducts = !isLoading && !allProducts?.length;
    const isMobile = useBreakpoint('md', 'down');
    const containerRef = useRef<HTMLDivElement>(null);
    const isOnScreen = useIsOnScreen(containerRef, { persistOnceSeen: true });
    const products = allProducts.slice(0, maxNumProducts);

    useEffect(() => swiper?.update(), [swiper, products?.length, isLoading]);

    const trackedProductIds = useRef<Array<string>>([]);
    useEffect(() => {
      if (!isOnScreen) return;

      const filteredProducts: Array<AlgoliaProductHit> = products?.filter(
        p => p && !trackedProductIds.current.includes(p.sku)
      );
      filteredProducts.forEach(({ sku }) => trackedProductIds.current.push(sku));

      trackViewItemList({
        currency: getStoreCurrency(),
        listName: trackingListName,
        viewedItems: transformAlgoliaHitForTracking(filteredProducts),
      });
      postEvent({ name: 'component.carousel.view', data: filteredProducts });
    }, [products, isOnScreen, shouldTrackImpressions]);

    if (hasNoProducts && noResultsPlaceholder === null) return null;

    return (
      <div className={classNames(styles.container, className)} ref={containerRef}>
        {heading || linkProps ? (
          <div className={styles.headingContainer}>
            {heading ? <HeadingDivider heading={heading} /> : null}
            {linkProps ? <GoToLink {...linkProps} className={styles.linkDesktop} /> : null}
          </div>
        ) : null}
        {hasNoProducts ? (
          noResultsPlaceholder ?? <ProductCarouselNoResultsPlaceholder />
        ) : (
          <div className={styles.carouselContainer}>
            <Carousel
              spaceBetween={10}
              slidesPerView="auto"
              slidesPerGroup={2}
              slidesOffsetBefore={2}
              slidesOffsetAfter={2}
              showProgressBar
              showArrows={!isMobile}
              showPagination={false}
              hideGradients={isMobile}
              fullBleed={isMobile}
              className={carouselClassName}
              onSwiper={swiper => {
                onSwiper?.(swiper);
                setSwiper(swiper);
              }}
              {...rest}
            >
              {[extraSlideStart ?? undefined]
                .concat(
                  (isLoading ? LOADING_PRODUCTS : products).slice(0, maxNumProducts).map((product, i) => (
                    <ProductFallback
                      isLoading={isLoading}
                      product={product}
                      key={`${i}`}
                      productCardSkeletonProps={{ variant: 'tablet', ...productCardSkeletonProps }}
                    >
                      {product ? renderProduct({ hit: product, position: i + 1 }) : null}
                    </ProductFallback>
                  ))
                )
                .filter(Boolean)}
            </Carousel>
          </div>
        )}
        {linkProps ? <GoToLink {...linkProps} className={styles.linkMobile} /> : null}
        {ctaButton ? <div className={styles.ctaButton}>{ctaButton}</div> : null}
      </div>
    );
  }
);

export type PresentationProductCarouselProps = Omit<CarouselPresentationContainerProps, 'children'> &
  ProductCarouselProps;

export const PresentationProductCarousel: React.VFC<PresentationProductCarouselProps> = ({
  coverImageSrc,
  coverImageAlt,
  coverContent,
  calloutBtn,
  className,
  ...rest
}) => {
  return (
    <CarouselPresentationContainer
      coverImageSrc={coverImageSrc}
      coverImageAlt={coverImageAlt}
      coverContent={coverContent}
      calloutBtn={calloutBtn}
    >
      <ProductCarousel
        className={classNames(styles.presentationContainerCarousel, className)}
        backgroundColor={palette.primary.light}
        outerSpacing={4}
        fullBleed={false}
        noResultsPlaceholder={<ProductCarouselNoResultsPlaceholder className={styles.noResultsPlaceholder} />}
        {...rest}
      />
    </CarouselPresentationContainer>
  );
};

const ProductCarouselNoResultsPlaceholder = ({ className }: { className?: string }) => (
  <NoResultsPlaceholder
    className={className}
    icon="LecternIllustrated"
    title="Looks like this shelf is empty"
    contents="We’re restocking this section... in the meantime check out our other deals."
  />
);
